import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Building2,
  Calendar,
  DollarSign,
  CreditCard,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Package,
  Truck,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  PENDING: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  APPROVED: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  PARTIALLY_PAID: { color: 'bg-yellow-100 text-yellow-700', icon: DollarSign },
  PAID: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  DISPUTED: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const paymentMethodLabels: Record<string, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank Transfer',
  CREDIT_CARD: 'Credit Card',
  CHECK: 'Check',
  OTHER: 'Other',
};

export function SupplierInvoiceDetailPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);

  const { data: invoice, isLoading, error } = trpc.tradingSupplierInvoice.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const approveMutation = trpc.tradingSupplierInvoice.approve.useMutation({
    onSuccess: () => {
      utils.tradingSupplierInvoice.byId.invalidate(id);
      utils.tradingSupplierInvoice.list.invalidate();
      showSuccessToast(t('finance.supplierInvoices.approved'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const cancelMutation = trpc.tradingSupplierInvoice.cancel.useMutation({
    onSuccess: () => {
      utils.tradingSupplierInvoice.byId.invalidate(id);
      utils.tradingSupplierInvoice.list.invalidate();
      showSuccessToast(t('finance.supplierInvoices.cancelled'));
      setCancelDialogOpen(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const disputeMutation = trpc.tradingSupplierInvoice.dispute.useMutation({
    onSuccess: () => {
      utils.tradingSupplierInvoice.byId.invalidate(id);
      utils.tradingSupplierInvoice.list.invalidate();
      showSuccessToast(t('finance.supplierInvoices.disputed'));
      setDisputeDialogOpen(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return t('finance.supplierInvoices.statusPending');
      case 'APPROVED':
        return t('finance.supplierInvoices.statusApproved');
      case 'PARTIALLY_PAID':
        return t('finance.supplierInvoices.statusPartiallyPaid');
      case 'PAID':
        return t('finance.supplierInvoices.statusPaid');
      case 'DISPUTED':
        return t('finance.supplierInvoices.statusDisputed');
      case 'CANCELLED':
        return t('finance.supplierInvoices.statusCancelled');
      default:
        return status;
    }
  };

  const handleApprove = () => {
    if (id) {
      approveMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    if (id) {
      cancelMutation.mutate({ id, reason: 'Cancelled by user' });
    }
  };

  const handleDispute = () => {
    if (id) {
      disputeMutation.mutate({ id, reason: 'Disputed by user' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/trading/finance/supplier-invoices')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('finance.supplierInvoices.backToList')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('finance.supplierInvoices.notFound')}</p>
        </div>
      </div>
    );
  }

  const config = statusConfig[invoice.status] ?? statusConfig.PENDING;
  const StatusIcon = config!.icon;
  const paymentProgress = invoice.total > 0
    ? Math.round((invoice.paidAmount / invoice.total) * 100)
    : 0;

  // Get items from either purchase order or goods receipt
  const items = invoice.goodsReceipt?.items || invoice.purchaseOrder?.items || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/finance/supplier-invoices')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('finance.supplierInvoices.backToList')}
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{invoice.invoiceNumber}</h1>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config!.color}`}
              >
                <StatusIcon className="h-3 w-3" />
                {getStatusLabel(invoice.status)}
              </span>
              {invoice.isOverdue && (
                <span className="bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                  {t('finance.supplierInvoices.overdue')}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {t('finance.supplierInvoices.title')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {invoice.status === 'PENDING' && (
            <Button onClick={handleApprove} disabled={approveMutation.isPending}>
              <CheckCircle className="mr-2 h-4 w-4" />
              {t('finance.supplierInvoices.approve')}
            </Button>
          )}
          {['APPROVED', 'PARTIALLY_PAID'].includes(invoice.status) && (
            <Button onClick={() => navigate(`/trading/finance/payments/new?supplierInvoiceId=${invoice.id}`)}>
              <CreditCard className="mr-2 h-4 w-4" />
              {t('finance.supplierInvoices.recordPayment')}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invoice.status === 'PENDING' && (
                <DropdownMenuItem onClick={() => navigate(`/trading/finance/supplier-invoices/${invoice.id}/edit`)}>
                  <FileText className="mr-2 h-4 w-4" />
                  {tCommon('edit')}
                </DropdownMenuItem>
              )}
              {['PENDING', 'APPROVED', 'PARTIALLY_PAID'].includes(invoice.status) && (
                <DropdownMenuItem onClick={() => setDisputeDialogOpen(true)}>
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  {t('finance.supplierInvoices.markDisputed')}
                </DropdownMenuItem>
              )}
              {invoice.status === 'PENDING' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    {tCommon('cancel')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmationDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title={t('finance.supplierInvoices.cancelInvoice')}
        description={t('finance.supplierInvoices.cancelConfirm')}
        confirmLabel={tCommon('cancel')}
        variant="destructive"
        onConfirm={handleCancel}
        isLoading={cancelMutation.isPending}
      />

      {/* Dispute Confirmation Dialog */}
      <ConfirmationDialog
        open={disputeDialogOpen}
        onOpenChange={setDisputeDialogOpen}
        title={t('finance.supplierInvoices.disputeInvoice')}
        description={t('finance.supplierInvoices.disputeConfirm')}
        confirmLabel={t('finance.supplierInvoices.markDisputed')}
        variant="destructive"
        onConfirm={handleDispute}
        isLoading={disputeMutation.isPending}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Invoice Summary Card */}
          <div className="border border-border bg-card p-6">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('finance.supplierInvoices.invoiceDate')}</p>
                <p className="mt-1 font-medium">{formatDate(invoice.invoiceDate)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('finance.supplierInvoices.dueDate')}</p>
                <p className={`mt-1 font-medium ${invoice.isOverdue ? 'text-red-600' : ''}`}>
                  {formatDate(invoice.dueDate)}
                  {invoice.isOverdue && <AlertTriangle className="ml-1 inline h-4 w-4" />}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('finance.supplierInvoices.total')}</p>
                <p className="mt-1 text-lg font-bold">
                  {formatCurrency(invoice.total, invoice.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('finance.supplierInvoices.balance')}</p>
                <p className={`mt-1 text-lg font-bold ${invoice.balanceDue <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {formatCurrency(invoice.balanceDue, invoice.currency)}
                </p>
              </div>
            </div>

            {/* Payment Progress */}
            <div className="mt-6">
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-muted-foreground">{t('finance.supplierInvoices.paymentProgress')}</span>
                <span className="font-medium">{paymentProgress}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden bg-gray-200">
                <div
                  className={`h-full transition-all ${paymentProgress >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(100, paymentProgress)}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                <span>{t('finance.supplierInvoices.paid')}: {formatCurrency(invoice.paidAmount, invoice.currency)}</span>
                <span>{t('finance.supplierInvoices.remaining')}: {formatCurrency(invoice.balanceDue, invoice.currency)}</span>
              </div>
            </div>
          </div>

          {/* Invoice Line Items */}
          <div className="border border-border bg-card">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold">{t('finance.supplierInvoices.lineItems')}</h2>
            </div>
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Package className="h-8 w-8 text-muted-foreground/50" />
                <p className="mt-2 text-sm text-muted-foreground">{t('finance.supplierInvoices.noItems')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium">{t('finance.supplierInvoices.product')}</th>
                      <th className="px-6 py-3 text-right text-sm font-medium">{t('finance.supplierInvoices.quantity')}</th>
                      <th className="px-6 py-3 text-right text-sm font-medium">{t('finance.supplierInvoices.unitPrice')}</th>
                      <th className="px-6 py-3 text-right text-sm font-medium">{t('finance.supplierInvoices.lineTotal')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((item: any) => (
                      <tr key={item.id} className="hover:bg-muted/30">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium">
                              {item.product?.model?.brand?.name} {item.product?.model?.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {item.product?.sku}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">{item.quantity}</td>
                        <td className="px-6 py-4 text-right">
                          {formatCurrency(Number(item.unitPrice), invoice.currency)}
                        </td>
                        <td className="px-6 py-4 text-right font-medium">
                          {formatCurrency(Number(item.quantity) * Number(item.unitPrice), invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals */}
            <div className="border-t bg-muted/30 px-6 py-4">
              <div className="flex flex-col items-end space-y-2">
                <div className="flex w-64 justify-between">
                  <span className="text-muted-foreground">{t('finance.supplierInvoices.subtotal')}</span>
                  <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                </div>
                {invoice.taxAmount > 0 && (
                  <div className="flex w-64 justify-between">
                    <span className="text-muted-foreground">{t('finance.supplierInvoices.tax')}</span>
                    <span>{formatCurrency(invoice.taxAmount, invoice.currency)}</span>
                  </div>
                )}
                <div className="flex w-64 justify-between border-t pt-2 text-lg font-bold">
                  <span>{t('finance.supplierInvoices.total')}</span>
                  <span>{formatCurrency(invoice.total, invoice.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className="border border-border bg-card">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">{t('finance.supplierInvoices.payments')}</h2>
              {['APPROVED', 'PARTIALLY_PAID'].includes(invoice.status) && (
                <Button size="sm" onClick={() => navigate(`/trading/finance/payments/new?supplierInvoiceId=${invoice.id}`)}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t('finance.supplierInvoices.recordPayment')}
                </Button>
              )}
            </div>
            {invoice.payments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CreditCard className="h-8 w-8 text-muted-foreground/50" />
                <p className="mt-2 text-sm text-muted-foreground">{t('finance.supplierInvoices.noPayments')}</p>
              </div>
            ) : (
              <div className="divide-y">
                {invoice.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center bg-green-100">
                        <DollarSign className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {formatCurrency(payment.amount, invoice.currency)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {paymentMethodLabels[payment.paymentMethod] || payment.paymentMethod}
                          {payment.reference && ` - ${payment.reference}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{payment.paymentNumber}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(payment.paymentDate)}
                      </p>
                      {payment.createdBy && (
                        <p className="text-xs text-muted-foreground">
                          {payment.createdBy}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border border-border bg-card p-6">
              <h3 className="mb-3 font-semibold">{t('finance.supplierInvoices.notes')}</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {invoice.notes}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Supplier Card */}
          <div className="border border-border bg-card p-6">
            <h3 className="mb-4 font-semibold">{t('finance.supplierInvoices.supplier')}</h3>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <Link
                  to={`/trading/suppliers/${invoice.supplierId}`}
                  className="font-medium text-primary hover:underline"
                >
                  {invoice.supplier.companyName}
                </Link>
                {(invoice.supplier as any).email && (
                  <p className="text-sm text-muted-foreground">{(invoice.supplier as any).email}</p>
                )}
                {(invoice.supplier as any).phone && (
                  <p className="text-sm text-muted-foreground">{(invoice.supplier as any).phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Purchase Order Reference */}
          {invoice.purchaseOrder && (
            <div className="border border-border bg-card p-6">
              <h3 className="mb-4 font-semibold">{t('finance.supplierInvoices.purchaseOrder')}</h3>
              <Link
                to={`/trading/purchasing/${invoice.purchaseOrderId}`}
                className="flex items-center gap-3 border border-border p-3 hover:bg-muted/50"
              >
                <div className="flex h-10 w-10 items-center justify-center bg-blue-100">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{invoice.purchaseOrder.orderNumber}</p>
                  <p className="text-sm text-muted-foreground">
                    {invoice.purchaseOrder.items?.length || 0} {t('finance.supplierInvoices.items')}
                  </p>
                </div>
              </Link>
            </div>
          )}

          {/* Goods Receipt Reference */}
          {invoice.goodsReceipt && (
            <div className="border border-border bg-card p-6">
              <h3 className="mb-4 font-semibold">{t('finance.supplierInvoices.goodsReceipt')}</h3>
              <Link
                to={`/trading/purchasing/receipts/${invoice.goodsReceiptId}`}
                className="flex items-center gap-3 border border-border p-3 hover:bg-muted/50"
              >
                <div className="flex h-10 w-10 items-center justify-center bg-green-100">
                  <Truck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{invoice.goodsReceipt.receiptNumber}</p>
                  <p className="text-sm text-muted-foreground">
                    {invoice.goodsReceipt.items?.length || 0} {t('finance.supplierInvoices.items')}
                  </p>
                </div>
              </Link>
            </div>
          )}

          {/* Created By */}
          {invoice.createdBy && (
            <div className="border border-border bg-card p-6">
              <h3 className="mb-4 font-semibold">{t('finance.supplierInvoices.createdBy')}</h3>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-gray-100">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium">
                    {invoice.createdBy.firstName} {invoice.createdBy.lastName}
                  </p>
                  <p className="text-sm text-muted-foreground">{invoice.createdBy.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border border-border bg-card p-6">
            <h3 className="mb-4 font-semibold">{t('finance.supplierInvoices.quickActions')}</h3>
            <div className="space-y-2">
              {invoice.status === 'PENDING' && (
                <Button className="w-full justify-start" onClick={handleApprove} disabled={approveMutation.isPending}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t('finance.supplierInvoices.approve')}
                </Button>
              )}
              {['APPROVED', 'PARTIALLY_PAID'].includes(invoice.status) && (
                <Button className="w-full justify-start" onClick={() => navigate(`/trading/finance/payments/new?supplierInvoiceId=${invoice.id}`)}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t('finance.supplierInvoices.recordPayment')}
                </Button>
              )}
              {invoice.purchaseOrder && (
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate(`/trading/purchasing/${invoice.purchaseOrderId}`)}>
                  <FileText className="mr-2 h-4 w-4" />
                  {t('finance.supplierInvoices.viewPurchaseOrder')}
                </Button>
              )}
              {invoice.goodsReceipt && (
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate(`/trading/purchasing/receipts/${invoice.goodsReceiptId}`)}>
                  <Truck className="mr-2 h-4 w-4" />
                  {t('finance.supplierInvoices.viewGoodsReceipt')}
                </Button>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="border border-border bg-card p-6">
            <h3 className="mb-4 font-semibold">{t('finance.supplierInvoices.timeline')}</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex h-6 w-6 items-center justify-center bg-green-100">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{t('finance.supplierInvoices.created')}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(invoice.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
              {invoice.receivedDate && (
                <div className="flex gap-3">
                  <div className="flex h-6 w-6 items-center justify-center bg-blue-100">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t('finance.supplierInvoices.received')}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(invoice.receivedDate)}
                    </p>
                  </div>
                </div>
              )}
              {invoice.status === 'PAID' && (
                <div className="flex gap-3">
                  <div className="flex h-6 w-6 items-center justify-center bg-green-100">
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t('finance.supplierInvoices.paidInFull')}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(invoice.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
